'use client';

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { Brand } from '../../api/spareparts-api';
import { getBrands, SPAREPARTS_API_KEY } from '../../api/spareparts-api';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { Button } from '../ui/button';
import type { SparepartsFormValues } from './spareparts-wrapper';

// Local image mapping for brands
const BRAND_IMAGE_MAP: Record<string, string> = {
  KTM: '/images/spareparts/KTM-Logo.png',
};

interface BrandSelectionProps {
  className?: string;
  onBrandChange: (brand: Brand) => void;
}

function BrandLogo({ brand }: { brand: Brand }) {
  const [hasFailed, setHasFailed] = useState(false);
  const src = BRAND_IMAGE_MAP[brand.name || ''] || brand.logoPath || brand.logoGrayscalePath;

  if (!src || hasFailed) return null;

  return (
    <div className="relative h-10 w-full">
      <Image
        src={src}
        alt=""
        fill
        sizes="160px"
        className="object-contain"
        onError={() => setHasFailed(true)}
      />
    </div>
  );
}

export function BrandSelection({ className, onBrandChange }: BrandSelectionProps) {
  const form = useFormContext<SparepartsFormValues>();
  const apiKeys = useGlobalStore(state => state.apiKeys);
  const apiKey = (apiKeys as any)?.spareparts || SPAREPARTS_API_KEY;
  const selectedBrand = form.watch('selectedBrand');
  const brandId = form.watch('brandId');

  const brandsQuery = useQuery({
    queryKey: ['spareparts', 'brands', apiKey],
    queryFn: async () => {
      const result = await getBrands({ apiKey });
      if (!result.success || !Array.isArray(result.data)) {
        throw new Error(result.message || 'Značky sa nepodarilo načítať. Skúste to znova.');
      }
      return [...result.data].sort(
        (a, b) =>
          (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
          (a.name || a.shortName || '').localeCompare(b.name || b.shortName || ''),
      );
    },
    retry: false,
  });

  // A VIN or direct model link can resolve the brand before the brand list loads.
  useEffect(() => {
    if (!brandId || selectedBrand?.id === brandId) return;
    const resolvedBrand = brandsQuery.data?.find(brand => brand.id === brandId);
    if (resolvedBrand) form.setValue('selectedBrand', resolvedBrand);
  }, [brandId, brandsQuery.data, selectedBrand?.id, form]);

  const handleBrandSelect = (brand: Brand) => {
    if (selectedBrand?.id === brand.id) return;
    form.setValue('selectedBrand', brand);
    form.setValue('brandId', brand.id);
    onBrandChange(brand);
  };

  if (brandsQuery.isLoading) {
    return (
      <div className={cn('space-y-3', className)} role="status">
        <p className="text-sm font-medium">Načítavam značky pre OEM katalóg…</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" aria-hidden="true">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-gray-100" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <fieldset className={cn('space-y-3', className)}>
      <legend className="text-base font-semibold">Značka motorky</legend>
      <p className="text-sm text-gray-600">Vyberte značku pre OEM katalóg.</p>
      {brandsQuery.isError ? (
        <div className="space-y-3 rounded-lg border border-red-200 p-4" role="alert">
          <p className="text-sm">{brandsQuery.error.message}</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => brandsQuery.refetch()}
            disabled={brandsQuery.isFetching}
          >
            Skúsiť znova
          </Button>
        </div>
      ) : brandsQuery.data?.length === 0 ? (
        <p className="text-sm text-gray-600" role="status">
          OEM katalóg momentálne neponúka žiadne značky.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {brandsQuery.data?.map(brand => (
            <button
              key={brand.id}
              type="button"
              onClick={() => handleBrandSelect(brand)}
              className={cn(
                'flex min-h-[104px] flex-col items-center justify-center gap-3 rounded-lg border-2 p-4 transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                selectedBrand?.id === brand.id
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-gray-300',
              )}
              title={brand.name || brand.shortName || 'Značka'}
              aria-pressed={selectedBrand?.id === brand.id}
            >
              <BrandLogo brand={brand} />
              <span className="text-center text-sm font-semibold">
                {brand.name || brand.shortName || 'Značka'}
              </span>
            </button>
          ))}
        </div>
      )}
    </fieldset>
  );
}
